import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'

export interface Filtros {
  semana?: string
  ruta?: string
  q: string
  page: number
}

type ClaveFiltro = 'semana' | 'ruta' | 'q'

// El buscador de texto es el unico filtro que se dispara con cada pulsacion
// de tecla (con un rebote de 300ms). Si apilara una entrada de historial por
// cambio, escribir "marisol" dejaria "mar", "maris", "marisol" en la pila y
// el usuario tendria que pulsar "atras" tres veces para deshacer una sola
// busqueda. `semana`, `ruta` y el cambio de pagina, en cambio, son acciones
// discretas y deliberadas: cada una merece su propia entrada, tal y como se
// espera al pulsar "atras". No simplificar esto a un solo modo para todos
// los filtros sin tener en cuenta esa diferencia.
const CLAVES_QUE_SUSTITUYEN_HISTORIAL: ReadonlySet<ClaveFiltro> = new Set(['q'])

function leerPagina(valor: string | null): number {
  const numero = Number.parseInt(valor ?? '', 10)
  return Number.isFinite(numero) && numero >= 1 ? numero : 1
}

export function useFiltrosUrl() {
  const [parametros, setParametros] = useSearchParams()

  const filtros = useMemo<Filtros>(
    () => ({
      semana: parametros.get('semana') ?? undefined,
      ruta: parametros.get('ruta') ?? undefined,
      q: parametros.get('q') ?? '',
      page: leerPagina(parametros.get('page')),
    }),
    [parametros],
  )

  const cambiarFiltro = useCallback(
    (clave: ClaveFiltro, valor: string | undefined) => {
      const siguientes = new URLSearchParams(parametros)
      if (valor) siguientes.set(clave, valor)
      else siguientes.delete(clave)
      // Cambiar un filtro con la pagina 5 activa dejaria al usuario mirando
      // una pagina que quiza ya no existe en el nuevo resultado.
      siguientes.delete('page')
      setParametros(siguientes, { replace: CLAVES_QUE_SUSTITUYEN_HISTORIAL.has(clave) })
    },
    [parametros, setParametros],
  )

  const irAPagina = useCallback(
    (pagina: number) => {
      const siguientes = new URLSearchParams(parametros)
      if (pagina <= 1) siguientes.delete('page')
      else siguientes.set('page', String(pagina))
      // Cambiar de pagina es una accion deliberada del usuario: merece su
      // propia entrada de historial (ver comentario junto a
      // CLAVES_QUE_SUSTITUYEN_HISTORIAL).
      setParametros(siguientes, { replace: false })
    },
    [parametros, setParametros],
  )

  return { filtros, cambiarFiltro, irAPagina }
}
